<script setup>
import { computed } from 'vue'
import { ref, watch } from 'vue'
import { ACCESS_LEVELS, ACCESS_LABELS, ACCESS_DESCRIPTIONS, productsCovering, formatPrice, planKindLabel, planCtaLabel } from '@/Utils/siteAccess'
import FormCheckbox from '@/components/Atoms/FormCheckbox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconLock from '@/components/icon/IconLock.vue'
import SiteStripeConnect from './SiteStripeConnect.vue'

// Three-level access control (public / registered / paid) for a content
// item (access model v2: items only). For `paid`, lists the paywall products
// that cover `refs` and lets the owner include/exclude the item from any
// `scope: items` product (v-model:planIds) or create a new plan inline.
const props = defineProps({
	modelValue: {
		type: String,
		default: 'public',
	},
	planIds: {
		// public_ids of `scope: items` products that should cover this item
		type: Array,
		default: () => [],
	},
	products: {
		type: Array,
		default: () => [],
	},
	refs: {
		// refs identifying this item, e.g. ['item:12']
		type: Array,
		default: () => [],
	},
	compact: {
		type: Boolean,
		default: false,
	},
	// Stripe Connect gate (castr monetization): choosing "Paid" needs an
	// active account unless the entity is already paid.
	canMonetize: {
		type: Boolean,
		default: true,
	},
	stripe: {
		type: Object,
		default: null,
	},
	// The entity does not exist yet (content being created): plans are
	// linked right after the POST, so say so instead of "Nothing unlocks this".
	pending: {
		type: Boolean,
		default: false,
	},
	// Explicit alternative to ticking plans: only all-access plans unlock
	// this item (v-model:allAccessOnly). A paid item needs one or the other.
	allAccessOnly: {
		type: Boolean,
		default: false,
	},
})
const emit = defineEmits(['update:modelValue', 'update:planIds', 'update:allAccessOnly', 'create-plan', 'stripe-connected'])

const showStripePrompt = ref(false)

const level = computed({
	get: () => props.modelValue || 'public',
	set: (v) => emit('update:modelValue', v),
})

const choose = (value) => {
	if (value === 'paid' && !props.canMonetize && level.value !== 'paid') {
		showStripePrompt.value = true
		return
	}
	showStripePrompt.value = false
	level.value = value
}
watch(() => props.canMonetize, (ok) => { if (ok) showStripePrompt.value = false })
const onStripeConnected = () => {
	showStripePrompt.value = false
	emit('stripe-connected')
}

const activeProducts = computed(() => (props.products || []).filter((p) => p.active !== false && p.kind !== 'grant'))
const allAccess = computed(() => activeProducts.value.filter((p) => p.scope === 'site'))
const itemProducts = computed(() => activeProducts.value.filter((p) => p.scope === 'items'))
const selected = computed(() => new Set(props.planIds))
const covering = computed(() => productsCovering(activeProducts.value, props.refs))
// 'plans' = ticked scope:items plans · 'all' = only all-access plans · null = nothing chosen yet
const mode = computed(() => (props.allAccessOnly ? 'all' : selected.value.size ? 'plans' : null))
const mappingMissing = computed(() => level.value === 'paid' && !mode.value)

const toggle = (product, on) => {
	const set = new Set(props.planIds)
	if (on) set.add(product.public_id)
	else set.delete(product.public_id)
	emit('update:planIds', [...set])
	if (on && props.allAccessOnly) emit('update:allAccessOnly', false)
}
const chooseAllAccessOnly = () => {
	if (props.planIds.length) emit('update:planIds', [])
	emit('update:allAccessOnly', true)
}
const choosePlans = () => emit('update:allAccessOnly', false)
</script>

<template>
	<div>
		<div class="grid gap-2" :class="compact ? 'grid-cols-3' : 'grid-cols-1 sm:grid-cols-3'">
			<button v-for="value in ACCESS_LEVELS" :key="value" type="button"
				class="rounded-lg border px-3 py-2 text-left transition-colors"
				:class="level === value ? 'border-main bg-main-transparent text-white' : 'border-surface-4 bg-surface-3 text-surface-8 hover:border-surface-6'"
				:data-needs-stripe="value === 'paid' && !canMonetize && level !== 'paid' ? 'true' : undefined"
				@click="choose(value)">
				<span class="block text-sm font-medium">
					{{ ACCESS_LABELS[value] }}
					<span v-if="value === 'paid' && !canMonetize && level !== 'paid'" class="ml-1 text-tiny font-normal text-warning">· needs Stripe</span>
				</span>
				<span v-if="!compact" class="block text-tiny text-surface-7">{{ ACCESS_DESCRIPTIONS[value] }}</span>
			</button>
		</div>

		<SiteStripeConnect v-if="showStripePrompt && !canMonetize" class="mt-3" compact :stripe="stripe || {}" action="make content paid" @connected="onStripeConnected" />
		<p v-if="level === 'paid' && !canMonetize" class="mb-0 mt-2 text-tiny text-warning">
			Stripe is not connected: this stays paid but nothing can be bought until you reconnect Stripe.
		</p>

		<div v-if="level === 'paid'" class="mt-3 rounded-lg bg-surface-3 p-3" data-testid="access-plans" :data-mode="mode || 'none'">
			<div class="flex items-center justify-between gap-2">
				<p class="mb-0 text-tiny font-medium text-surface-8">Unlocked by</p>
				<FormButton type="link" size="sm" class="!px-0" @click="emit('create-plan', refs)">+ New plan</FormButton>
			</div>

			<!-- All-access plans: read-only, they cover everything on the site -->
			<div v-if="allAccess.length" class="mt-2 flex items-start gap-2 rounded-md border border-dashed border-surface-5 bg-surface-2/60 px-3 py-2" data-testid="access-all-access">
				<icon-base class="mt-0.5 shrink-0 text-surface-7" :width="14" :height="14" fill="none"><icon-lock /></icon-base>
				<div class="min-w-0 text-tiny text-surface-7">
					<span class="font-medium text-surface-8">All-access {{ allAccess.length === 1 ? 'plan' : 'plans' }}</span>
					({{ allAccess.map((p) => p.title).join(', ') }}) always unlock this.
					<span class="block text-surface-6">Set on the plan itself — cannot be changed per item.</span>
				</div>
			</div>

			<!-- Per-item plans: tick the ones that should unlock this item -->
			<p class="mb-1 mt-3 text-tiny text-surface-7">Plans that unlock this item</p>
			<div v-if="itemProducts.length" class="flex flex-col gap-1 rounded-md bg-surface-2 p-1.5">
				<label v-for="product in itemProducts" :key="product.public_id"
					class="flex items-center gap-2 rounded px-2 py-1.5 text-sm text-surface-text-1 transition-colors hover:bg-surface-3"
					:class="selected.has(product.public_id) ? 'bg-main-transparent' : ''">
					<FormCheckbox :name="`access-plan-${product.public_id}`" :model-value="selected.has(product.public_id)"
						@update:model-value="toggle(product, $event)" />
					<span class="min-w-0 truncate">{{ product.title }}</span>
					<span class="ml-auto shrink-0 text-tiny text-surface-7">{{ planKindLabel(product) }} · {{ formatPrice(product) }}</span>
					<span class="shrink-0 rounded bg-surface-4 px-1.5 py-0.5 text-tiny text-surface-8" :title="`Viewers see a “${planCtaLabel(product)}” button`">{{ planCtaLabel(product) }}</span>
				</label>
			</div>
			<p v-else class="mb-0 rounded-md bg-surface-2 px-3 py-2 text-tiny text-surface-7">No per-item plans yet — use “+ New plan” to create one.</p>

			<!-- The explicit alternative: no per-item plan, only all-access plans -->
			<label class="mt-2 flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 text-sm text-surface-text-1 transition-colors hover:bg-surface-2"
				:class="mode === 'all' ? 'bg-main-transparent' : ''">
				<input type="radio" name="access-mapping" class="mt-1 size-3.5 shrink-0 accent-main" :checked="mode === 'all'" data-testid="access-all-access-only" @change="chooseAllAccessOnly">
				<span class="min-w-0">
					<span class="block">Only all-access plans unlock this</span>
					<span class="block text-tiny text-surface-7">
						<template v-if="allAccess.length">No per-item plan; viewers need {{ allAccess.map((p) => p.title).join(' or ') }}.</template>
						<template v-else>There is no all-access plan yet — the item stays locked with nothing to buy until you create one under Plans.</template>
					</span>
				</span>
			</label>
			<button v-if="mode === 'all' && itemProducts.length" type="button" class="mt-1 text-tiny text-surface-8 underline hover:text-white" data-testid="access-choose-plans" @click="choosePlans">Pick specific plans instead</button>

			<p v-if="mappingMissing" class="mb-0 mt-2 text-tiny text-warning" data-testid="access-plans-required">
				<template v-if="pending">Tick the plans that unlock this item, or choose “Only all-access plans”. Plans are linked as soon as the item is created.</template>
				<template v-else>Tick the plans that unlock this item, or choose “Only all-access plans”.</template>
			</p>
			<p v-else-if="mode === 'all' && !allAccess.length && !covering.length" class="mb-0 mt-2 text-tiny text-warning">Nothing unlocks this yet.</p>
		</div>
	</div>
</template>
